import { ICommand, ICommandHandler } from '@withlocals/cqrs.js/lib';
import Analytics from '@withlocals/withlocals-analytics';
import {
  signInGoogleApp,
  signInGoogleWeb,
} from 'aggregates/authentication/googleauth';
import { Observable } from 'rxjs';
import { asap } from 'rxjs/scheduler/asap';
import { WlWindow } from 'typings/globals/cordova';
import { isCordova } from 'utils/platform';

import { history } from '../../wlHistory';
import { AjaxClient, ErrorResponse } from '../AjaxClient';
import { NativeStorageProxy } from './NativeStorage';
import {
  BecomePuppet,
  BecomeYourself,
  LoginWithAuthInfo,
  LoginWithEmail,
  LoginWithFacebook,
  LoginWithGoogle,
  LoginWithOAuth2,
  LoginWithPasswordToken,
  Logout,
  OAuth2Info,
  ResetPassword,
  Signup,
} from './commands';
import {
  AuthenticatedEvent,
  AuthenticationChangedEvent,
  AuthenticationErrorEvent,
  NotAuthenticatedEvent,
  PasswordResetEvent,
} from './events';
import { CordovaFbLogin, WebFbLogin } from './facebook';

declare const window: WlWindow;
const client = new AjaxClient();

const getAnalyticsCategory = (): string =>
  window.previousNonAuthPageCategory || window.currentPageCategory || 'App';

const afterLogin = (method: string, event: AuthenticatedEvent) => {
  Analytics.track(getAnalyticsCategory(), 'Logged in', method);

  const key = 'FirstTimeLogin';
  if (isCordova()) {
    const storage = new NativeStorageProxy();
    storage.getOrElse(key, false).then((isNotFirstlogin) => {
      if (!isNotFirstlogin) {
        storage.set(key, true).then(() => {
          Analytics.track(getAnalyticsCategory(), 'First log in', method);
        });
      }
    });
  } else {
    try {
      if (window.localStorage.getItem(key) !== 'true') {
        window.localStorage.setItem(key, 'true');
        Analytics.track(getAnalyticsCategory(), 'First log in', method);
      }
    } catch (e) {}
  }
};

const loginWithOAuth2Handler = (command: LoginWithOAuth2) => {
  const url = `${URL_API_HOST}/api/v1/authentication/${command.provider}`;
  const email = !!command.oauthInfo.email
    ? command.oauthInfo.email.trim()
    : command.oauthInfo.email;
  const body = {
    email,
    companyName: command.oauthInfo.companyName,
    info: command.oauthInfo,
  };

  return client
    .post(url, body)
    .map((resp) =>
      resp instanceof ErrorResponse
        ? new AuthenticationErrorEvent(resp.error)
        : new AuthenticatedEvent(resp.response),
    )
    .catch((error) => Observable.of(new AuthenticationErrorEvent(error)))
    .do((event) => {
      if (event instanceof AuthenticationErrorEvent) {
        Analytics.track(
          getAnalyticsCategory(),
          'Log in',
          'Error',
          event.pretty(),
        );
      } else {
        Analytics.track(getAnalyticsCategory(), 'Log in', command.provider);
        afterLogin(command.provider, event);
      }
    });
};

const loginWithEmailHandler = (command: LoginWithEmail) => {
  const email = !!command.email ? command.email.trim() : command.email;

  const params = new URLSearchParams();
  params.set('username', email);
  params.set('password', command.password());
  params.set('callbackUrl', '');
  params.set('csrfToken', command.csrfToken);
  params.set('json', 'true');

  // const signinResponse = await fetch(`${URL_STOREFRONT_HOST}/api/auth/callback/credentials`, {
  //   method: 'POST',
  //   headers: {
  //     'Accept': '*/*',
  //     'Content-Type': 'application/x-www-form-urlencoded'
  //   },
  //   credentials: 'include',
  //   body: params.toString()
  // });

  const url = `${URL_AUTHENTICATION_HOST}/api/auth/callback/credentials`;
  const body = params.toString();
  const headers = {
    Accept: '*/*',
    'Content-Type': 'application/x-www-form-urlencoded',
  };

  return client
    .post(url, body, headers)
    .map((resp) =>
      resp instanceof ErrorResponse
        ? new AuthenticationErrorEvent(resp.error)
        : new AuthenticatedEvent(resp.response),
    )
    .catch((error) => Observable.of(new AuthenticationErrorEvent(error)))
    .do((event) => {
      if (event instanceof AuthenticationErrorEvent) {
        Analytics.track(
          getAnalyticsCategory(),
          'Log in',
          'Error',
          event.pretty(),
        );
      } else {
        Analytics.track(getAnalyticsCategory(), 'Log in', 'Email');
        afterLogin('Email', event);
      }
    });
};

const resetPasswordHandler = (command: ResetPassword) => {
  const url = `${URL_HOST}/reset/`;
  const body = {
    email: command.email,
  };
  return client
    .post(url, body)
    .map((resp) => {
      console.debug(resp);
      return resp.response && resp.response.error
        ? new AuthenticationErrorEvent(resp.response.error)
        : new PasswordResetEvent();
    })
    .catch((error) => Observable.of(new AuthenticationErrorEvent(error)))
    .do((event) => {
      if (event instanceof AuthenticationErrorEvent) {
        Analytics.track(getAnalyticsCategory(), 'Error', event.pretty());
      } else {
        Analytics.track(getAnalyticsCategory(), 'Reset password');
      }
    });
};

const signupHandler = (command: Signup) => {
  const url = `${URL_API_HOST}/api/v1/authentication/signup`;
  const body = {
    firstName: command.firstName,
    lastName: command.lastName,
    email: command.email,
    gender: command.gender,
    phoneNumber: command.phoneNumber,
    country: command.country,
    password: command.password(),
    companyName: command.companyName,
  };

  return client
    .post(url, body)
    .map((resp) =>
      resp instanceof ErrorResponse
        ? new AuthenticationErrorEvent(resp.error)
        : new AuthenticatedEvent(resp.response),
    )
    .catch((error) => Observable.of(new AuthenticationErrorEvent(error)))
    .do((event) => {
      if (event instanceof AuthenticationErrorEvent) {
        Analytics.track(
          getAnalyticsCategory(),
          'Sign up',
          'Error',
          event.pretty(),
        );
      } else {
        Analytics.track(getAnalyticsCategory(), 'Sign up', 'Email');
        afterLogin('Email', event);
      }
    });
};

const becomeYourselfHandler = (command: BecomeYourself) => {
  const url = `${URL_API_HOST}/api/v1/account/reset`;
  const body = {};

  return client.post(url, body).map((resp) => new AuthenticationChangedEvent());
};

const becomePuppetHandler = (command: BecomePuppet) => {
  const url = `${URL_API_HOST}/api/v1/account/current`;
  const body = { id: command.id };

  return client.post(url, body).map((resp) => new AuthenticationChangedEvent());
};

const loginWithFacebookHandler = (command: LoginWithFacebook) => {
  const fb = isCordova() ? new CordovaFbLogin() : new WebFbLogin();

  const afterFbLogin = (loginResult): Promise<OAuth2Info> => {
    if (loginResult.status === 'connected') {
      console.debug('[Facebook] - connected');
      const loginAuthResponse = loginResult.authResponse;
      const authResponse: OAuth2Info = {
        ...loginAuthResponse,
        expiresIn: parseInt(loginResult.authResponse.expiresIn, 10),
      };
      return fb.getEmail().then((email) => ({
        email,
        companyName: command.companyName,
        ...authResponse,
      }));
    }
    if (loginResult.status === 'not_authorized') {
      console.debug('[Facebook] - not_authorized');
      return Promise.reject('Please authenticate this app in Facebook');
    }
    console.debug('[Facebook] - unknown');
    return Promise.reject('Please login into Facebook first');
  };
  return Observable.fromPromise(
    fb
      .getLoginStatus()
      .then((loginResult) => {
        if (
          (loginResult.status !== 'connected' &&
            loginResult.status !== 'not_authorized') ||
          !loginResult.authResponse
        ) {
          return fb.login(true);
        }
        return loginResult as PhonegapFacebookPlugin.LoginResult;
      })
      .then(afterFbLogin),
    asap,
  )
    .do((authResponse) => console.log('authResponse: ', authResponse))
    .flatMap((authResponse) =>
      loginWithOAuth2Handler(new LoginWithOAuth2('facebook', authResponse)),
    )
    .catch((error) => Observable.of(new AuthenticationErrorEvent(error)));
};

const loginWithGoogleHandler = (command: LoginWithGoogle) => {
  const oauthInfo = isCordova()
    ? signInGoogleApp(command.companyName)
    : signInGoogleWeb(command.companyName);
  return Observable.fromPromise(oauthInfo, asap)
    .flatMap((authResponse) =>
      loginWithOAuth2Handler(new LoginWithOAuth2('google', authResponse)),
    )
    .catch((error) => Observable.of(new AuthenticationErrorEvent(error)));
};

const logoutHandler = (command: Logout) => {
  const url = `${URL_API_HOST}/api/v1/authentication/logout`;
  const body = {};

  return client.post(url, body).map((resp) => {
    if (isCordova()) {
      history.push('/');
    }
    return new NotAuthenticatedEvent();
  });
};

const loginWithPasswordToken = (command: LoginWithPasswordToken) => {
  const url = `${URL_API_HOST}/api/v1/authentication/tokenlogin?logintoken=${command.token}`;
  return client
    .post(url, {})
    .map((resp) =>
      resp instanceof ErrorResponse
        ? new AuthenticationErrorEvent(resp.error)
        : new AuthenticatedEvent(resp.response),
    )
    .catch((error) => Observable.of(new AuthenticationErrorEvent(error)))
    .do((event) => {
      if (event instanceof AuthenticationErrorEvent) {
        Analytics.track(
          getAnalyticsCategory(),
          'Log in',
          'Error',
          event.pretty(),
        );
      } else {
        Analytics.track(getAnalyticsCategory(), 'Log in', 'PasswordToken');
        afterLogin('PasswordToken', event);
      }
    });
};

const loginWithAuthInfo = (command: LoginWithAuthInfo) => {
  return Observable.of(new AuthenticatedEvent(command.authInfo));
};

export const authMessageHandlerFactory = (): ICommandHandler[] => {
  const handler = (command: ICommand) => {
    if (command instanceof LoginWithOAuth2) {
      return loginWithOAuth2Handler(command);
    }
    if (command instanceof LoginWithEmail) {
      return loginWithEmailHandler(command);
    }
    if (command instanceof Logout) {
      return logoutHandler(command);
    }
    if (command instanceof Signup) {
      return signupHandler(command);
    }
    if (command instanceof BecomeYourself) {
      return becomeYourselfHandler(command);
    }
    if (command instanceof BecomePuppet) {
      return becomePuppetHandler(command);
    }
    if (command instanceof LoginWithFacebook) {
      return loginWithFacebookHandler(command);
    }
    if (command instanceof ResetPassword) {
      return resetPasswordHandler(command);
    }
    if (command instanceof LoginWithPasswordToken) {
      return loginWithPasswordToken(command);
    }
    if (command instanceof LoginWithAuthInfo) {
      return loginWithAuthInfo(command);
    }
    if (command instanceof LoginWithGoogle) {
      return loginWithGoogleHandler(command);
    }
    return null;
  };

  return [handler];
};
